/**
 * FieldDetailsStrip
 * --------------------------------------------------------
 * Horizontally scrolling strip of field metrics for the Farm View
 * "Field details" tab. Chevron buttons overlay the strip edges (with a
 * white fade) and page the content left/right; each arrow only shows while
 * there is more content in that direction. Which cells are visible depends
 * on the width — the strip simply clips and scrolls (large: everything
 * fits, small: ~5 cells).
 */
const React = require('react');
const PropTypes = require('prop-types');
require('./FieldDetailsStrip.css');

const { useRef, useState, useEffect, useCallback } = React;

const SCROLL_EDGE_TOLERANCE = 2;

const Chevron = ({ direction }) => (
  <svg width='6' height='10' viewBox='0 0 6 10' fill='none' aria-hidden='true'>
    <path
      d={direction === 'left' ? 'M5 1L1 5l4 4' : 'M1 1l4 4-4 4'}
      stroke='currentColor'
      strokeWidth='1.6'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

Chevron.propTypes = { direction: PropTypes.oneOf(['left', 'right']) };

const FieldDetailsStrip = ({ leading, children, prevLabel, nextLabel }) => {
  const scrollerRef = useRef(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollPrev(el.scrollLeft > SCROLL_EDGE_TOLERANCE);
    setCanScrollNext(
      el.scrollLeft + el.clientWidth < el.scrollWidth - SCROLL_EDGE_TOLERANCE,
    );
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    updateArrows();
    el.addEventListener('scroll', updateArrows, { passive: true });
    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateArrows);
      observer.observe(el);
      // The track is max-content wide, so it resizes whenever its content
      // changes (field switch, data arriving) — no per-render re-measure.
      if (el.firstElementChild) observer.observe(el.firstElementChild);
    } else {
      window.addEventListener('resize', updateArrows);
    }
    return () => {
      el.removeEventListener('scroll', updateArrows);
      if (observer) observer.disconnect();
      else window.removeEventListener('resize', updateArrows);
    };
  }, [updateArrows]);

  const page = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction * Math.max(el.clientWidth * 0.6, 120),
      behavior: 'smooth',
    });
  };

  return (
    <div className='field-details-strip'>
      <div className='field-details-strip__scroller' ref={scrollerRef}>
        <div className='field-details-strip__track'>
          {leading}
          {children}
        </div>
      </div>
      {canScrollPrev && (
        <button
          type='button'
          className='field-details-strip__arrow field-details-strip__arrow--prev'
          aria-label={prevLabel}
          onClick={() => page(-1)}
        >
          <span className='field-details-strip__arrow-button'>
            <Chevron direction='left' />
          </span>
        </button>
      )}
      {canScrollNext && (
        <button
          type='button'
          className='field-details-strip__arrow field-details-strip__arrow--next'
          aria-label={nextLabel}
          onClick={() => page(1)}
        >
          <span className='field-details-strip__arrow-button'>
            <Chevron direction='right' />
          </span>
        </button>
      )}
    </div>
  );
};

FieldDetailsStrip.propTypes = {
  leading: PropTypes.node,
  children: PropTypes.node,
  prevLabel: PropTypes.string,
  nextLabel: PropTypes.string,
};

module.exports = FieldDetailsStrip;
